import logger from '@/lib/logger';
import type { ToastVariant } from '@/components/ui/UnifiedToast';

/**
 * The seam that carries a retained toast to the notification bell.
 *
 * WHY A SINK AND NOT A DIRECT STORE IMPORT. `show-unified-toast.ts` is the
 * convergence point for three of the four toast entry points, and it carries no
 * `'use client'` marker — so importing the Redux store there would construct the
 * store in any server-rendered path that happens to pull it in. Instead a client
 * component (`NotificationInitializer`, mounted app-wide by `AppShellProvider`)
 * registers a sink, and the convergence point only knows how to hand a record
 * over. That keeps the toast layer free of store and React coupling and makes
 * this module unit-testable on its own.
 *
 * NOTHING IS DROPPED QUIETLY. Records raised before a sink registers are held
 * and flushed on registration — that window is the app's client boot. Two things
 * log at ERROR, because a lost record is the exact defect this change exists to
 * fix and a failsafe firing must be visible (owner's standing rule):
 *   - a record arriving with no sink registered once the document has finished
 *     loading, which is no longer a boot race but a missing initializer;
 *   - a sink that throws while recording.
 * There is no cap on the held records and no truncation of their content.
 */

export interface RetainedToastRecord {
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Stable toast id when the call site supplied one, for de-duplication. */
  id?: string;
  /** Where the toast was raised, for the bell entry's link back. */
  sourcePath?: string;
  raisedAt: string;
}

export type ToastSink = (record: RetainedToastRecord) => void;

let sink: ToastSink | null = null;
const held: RetainedToastRecord[] = [];

/**
 * Register the sink that carries retained toasts to the bell. Returns an
 * unregister function. Flushes anything raised during client boot.
 */
export function registerToastSink(next: ToastSink): () => void {
  sink = next;
  if (held.length > 0) {
    const pending = held.splice(0, held.length);
    for (const record of pending) deliver(record);
  }
  return () => {
    if (sink === next) sink = null;
  };
}

function deliver(record: RetainedToastRecord): void {
  if (!sink) return;
  try {
    sink(record);
  } catch (error) {
    logger.error('toastSink', 'A retained notification was lost — the sink threw', {
      title: record.title,
      variant: record.variant,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Hand a retained toast to the bell, or hold it until a sink registers. */
export function recordRetainedToast(record: RetainedToastRecord): void {
  if (sink) {
    deliver(record);
    return;
  }

  held.push(record);

  // Before the document finishes loading this is an ordinary boot race. After
  // it, no sink means no initializer is mounted and the record is going
  // nowhere — the silent-loss failure mode, so it is an ERROR.
  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    logger.error(
      'toastSink',
      'A retained notification has no sink to record it — NotificationInitializer is not mounted',
      { title: record.title, variant: record.variant, heldCount: held.length },
    );
  }
}

/** Test seam: the records still waiting for a sink. */
export function heldRetainedToasts(): readonly RetainedToastRecord[] {
  return held;
}

/** Test seam: drop registration and held records between cases. */
export function resetToastSinkForTests(): void {
  sink = null;
  held.length = 0;
}
